import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';

const WHATSAPP_NUMBER = "573189717723";
const WHATSAPP_DISPLAY = "+57 318 971 7723";

// Contact.jsx — Form + channels
const Contact = (() => {
  const { Arrow, Heart, Diamond, Bolt } = PixelIcons;

  const ctStyles = {
    section: { padding: "112px 0", position: "relative" },
    container: { maxWidth: 1280, margin: "0 auto", padding: "0 32px" },
    grid: { display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 56 },
    tag: {
      display: "inline-flex", alignItems: "center", gap: 12,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "11px", letterSpacing: "0.16em",
      color: "var(--ragross-magenta)",
    },
    title: {
      font: "var(--type-display-md)",
      letterSpacing: "-0.01em",
      lineHeight: 1.08,
      color: "#fff",
      marginTop: 18,
      marginBottom: 0,
    },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", marginTop: 18, maxWidth: 460 },
    channels: { marginTop: 32, display: "flex", flexDirection: "column", gap: 14 },
    channel: {
      display: "flex", alignItems: "center", gap: 16,
      padding: "14px 16px",
      background: "var(--bg-surface)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-card)",
      transition: "border-color 200ms, transform 200ms",
    },
    channelIcon: {
      width: 36, height: 36,
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(255,255,255,0.08)",
      display: "flex", alignItems: "center", justifyContent: "center",
      borderRadius: 2,
    },
    channelLabel: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.12em",
      color: "var(--fg-3)",
    },
    channelValue: {
      fontFamily: "'VT323', monospace",
      fontSize: 20, color: "#fff", lineHeight: 1.1,
    },
    form: {
      background: "var(--bg-surface)",
      border: "2px solid var(--ragross-magenta)",
      borderRadius: "var(--radius-card)",
      padding: 28,
      boxShadow: "var(--shadow-pixel-cy), var(--shadow-elev)",
      position: "relative",
    },
    formTag: {
      position: "absolute",
      top: -12, left: 24,
      padding: "4px 10px",
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(0,232,203,0.5)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px",
      letterSpacing: "0.16em",
      color: "var(--ragross-cyan)",
    },
    field: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 18 },
    label: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "10px", letterSpacing: "0.12em",
      color: "var(--ragross-cyan)",
    },
    input: {
      background: "var(--ragross-purple-night)",
      border: "1px solid var(--border-soft)",
      borderRadius: "var(--radius-soft)",
      padding: "12px 14px",
      color: "#fff",
      font: "var(--type-body)",
      outline: "none",
      transition: "border-color 200ms, box-shadow 200ms",
      fontFamily: "var(--font-body)",
    },
    twoCol: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
    submit: { marginTop: 12, width: "100%", justifyContent: "center" },
    legal: {
      fontFamily: "'VT323', monospace",
      fontSize: 16,
      color: "var(--fg-3)",
      marginTop: 16,
      textAlign: "center",
    },
    success: {
      padding: "14px 16px",
      background: "rgba(79,253,21,0.08)",
      border: "1px solid var(--ragross-green)",
      color: "var(--ragross-green)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "10px",
      letterSpacing: "0.12em",
      borderRadius: 2,
      marginBottom: 18,
      display: "flex", alignItems: "center", gap: 10,
    },
    error: {
      padding: "14px 16px",
      background: "rgba(253,20,145,0.08)",
      border: "1px solid var(--ragross-magenta)",
      color: "var(--ragross-magenta)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "10px",
      letterSpacing: "0.12em",
      borderRadius: 2,
      marginBottom: 18,
      display: "flex", alignItems: "center", gap: 10,
    },
  };

  function Input({ label, ...rest }) {
    const [focus, setFocus] = React.useState(false);
    return (
      <div style={ctStyles.field}>
        <label style={ctStyles.label}>▸ {label}</label>
        <input
          {...rest}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            ...ctStyles.input,
            borderColor: focus ? "var(--ragross-cyan)" : "var(--border-soft)",
            boxShadow: focus ? "var(--glow-cyan-sm)" : "none",
          }}
        />
      </div>
    );
  }

  function Select({ label, children, ...rest }) {
    const [focus, setFocus] = React.useState(false);
    return (
      <div style={ctStyles.field}>
        <label style={ctStyles.label}>▸ {label}</label>
        <select
          {...rest}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            ...ctStyles.input,
            borderColor: focus ? "var(--ragross-cyan)" : "var(--border-soft)",
            boxShadow: focus ? "var(--glow-cyan-sm)" : "none",
          }}
        >{children}</select>
      </div>
    );
  }

  function Textarea({ label, ...rest }) {
    const [focus, setFocus] = React.useState(false);
    return (
      <div style={ctStyles.field}>
        <label style={ctStyles.label}>▸ {label}</label>
        <textarea
          {...rest}
          rows={4}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          style={{
            ...ctStyles.input,
            borderColor: focus ? "var(--ragross-cyan)" : "var(--border-soft)",
            boxShadow: focus ? "var(--glow-cyan-sm)" : "none",
            resize: "vertical", minHeight: 100,
          }}
        />
      </div>
    );
  }

  function Contact() {
    const t = useT();
    const [status, setStatus] = React.useState("idle");
    const handle = async (e) => {
      e.preventDefault();
      const form = e.currentTarget;
      setStatus("sending");

      try {
        const response = await fetch("/contact.php", {
          method: "POST",
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok || !result.ok) throw new Error(result.error || "send_failed");

        form.reset();
        setStatus("sent");
        setTimeout(() => setStatus("idle"), 5000);
      } catch (error) {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 6000);
      }
    };

    return (
      <section id="contact" style={ctStyles.section} data-screen-label="09 Contacto">
        <div style={ctStyles.container}>
          <div style={ctStyles.grid} className="responsive-grid-2">
            <div>
              <div style={ctStyles.tag}>
                <span>{t("ct.tag")}</span>
                <span style={{ width: 60, height: 1, background: "linear-gradient(90deg, var(--ragross-magenta), transparent)" }}></span>
              </div>
              <h2 style={ctStyles.title} className="section-title">
                {t("ct.title1")}
                <br/>
                <span style={{ color: "var(--ragross-cyan)" }}>{t("ct.title2")}</span>
              </h2>
              <p style={ctStyles.lead}>{t("ct.lead")}</p>

              <div style={ctStyles.channels}>
                <div style={ctStyles.channel}>
                  <div style={ctStyles.channelIcon}><Heart size={16}/></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={ctStyles.channelLabel}>{t("ct.email")}</span>
                    <span style={ctStyles.channelValue}>proyectos@ragrosstudios.com</span>
                  </div>
                </div>
                <div style={ctStyles.channel}>
                  <div style={ctStyles.channelIcon}><Bolt size={16}/></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={ctStyles.channelLabel}>{t("ct.whatsapp")}</span>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ ...ctStyles.channelValue, textDecoration: "none" }}
                    >
                      {WHATSAPP_DISPLAY}
                    </a>
                  </div>
                </div>
                <div style={ctStyles.channel}>
                  <div style={ctStyles.channelIcon}><Diamond size={16}/></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={ctStyles.channelLabel}>{t("ct.location")}</span>
                    <span style={ctStyles.channelValue}>{t("ct.locationVal")}</span>
                  </div>
                </div>
              </div>
            </div>

            <form style={ctStyles.form} onSubmit={handle}>
              <span style={ctStyles.formTag}>{t("ct.formTag")}</span>

              {status === "sent" && (
                <div style={ctStyles.success}>
                  <Heart size={14}/>
                  <span>{t("ct.sent")}</span>
                </div>
              )}
              {status === "error" && (
                <div style={ctStyles.error}>
                  <Bolt size={14}/>
                  <span>{t("ct.error")}</span>
                </div>
              )}

              <div style={ctStyles.twoCol} className="responsive-grid-2">
                <Input label={t("ct.name")} name="name" placeholder={t("ct.namePh")} required/>
                <Input label={t("ct.email")} name="email" type="email" placeholder={t("ct.emailPh")} required/>
              </div>

              <Select label={t("ct.type")} name="project_type" required>
                <option>{t("ct.t1")}</option>
                <option>{t("ct.t2")}</option>
                <option>{t("ct.t3")}</option>
                <option>{t("ct.t4")}</option>
                <option>{t("ct.t5")}</option>
                <option>{t("ct.t6")}</option>
                <option>{t("ct.t7")}</option>
              </Select>

              <Input label={t("ct.budget")} name="budget" placeholder={t("ct.budgetPh")}/>

              <Textarea label={t("ct.msg")} name="message" placeholder={t("ct.msgPh")} required/>
              <input type="text" name="website" tabIndex="-1" autoComplete="off" style={{ display: "none" }} />

              <button type="submit" className="btn btn-primary" style={ctStyles.submit} disabled={status === "sending"}>
                <span>{status === "sending" ? t("ct.sending") : t("ct.submit")}</span>
                <Arrow size={11} color="#fff"/>
              </button>

              <div style={ctStyles.legal}>
                {"> "}{t("ct.legal")}<span className="blink">▮</span>
              </div>
            </form>
          </div>
        </div>
      </section>
    );
  }

  return Contact;
})();

export default Contact;
